import { cn } from '@/lib/utils';
import { useMutation } from 'convex/react';
import { Pencil } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '~/convex/_generated/api';
import type { Id } from '~/convex/_generated/dataModel';
import { isDefaultReportTitle } from '~/convex/lib/types';

interface EditableReportTitleProps {
  reportId: Id<'reports'>;
  title: string;
  className?: string;
  onEditingChange?: (isEditing: boolean) => void;
}

export const EditableReportTitle = ({
  reportId,
  title,
  className,
  onEditingChange,
}: EditableReportTitleProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState(title);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    onEditingChange?.(isEditing);
  }, [isEditing, onEditingChange]);

  const updateReport = useMutation(
    api.reports.updateReportTitle,
  ).withOptimisticUpdate((localStore, args) => {
    const current = localStore.getQuery(api.reports.get, { id: args.id });
    if (current != null) {
      localStore.setQuery(
        api.reports.get,
        { id: args.id },
        {
          ...current,
          patientName: args.patientName,
        },
      );
    }
  });

  // Sync external title changes (e.g. LLM auto-generation) when not editing
  useEffect(() => {
    if (!isEditing) {
      setLocalValue(title);
    }
  }, [title, isEditing]);

  const save = useCallback(
    (value: string) => {
      const trimmed = value.trim();
      if (trimmed.length > 0 && trimmed !== title) {
        updateReport({
          id: reportId,
          patientName: trimmed,
        });
      }
    },
    [reportId, title, updateReport],
  );

  const handleBlur = () => {
    setIsEditing(false);
    save(localValue);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      inputRef.current?.blur();
    } else if (e.key === 'Escape') {
      setLocalValue(title);
      setIsEditing(false);
    }
  };

  const startEditing = () => {
    setIsEditing(true);
    requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    });
  };

  const isDefault = isDefaultReportTitle(title);

  if (isEditing) {
    return (
      <input
        ref={inputRef}
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        className={cn(
          'flex-1 text-lg font-bold outline-none sm:text-2xl',
          'border-primary border-b-2',
          className,
        )}
      />
    );
  }

  return (
    <button
      type="button"
      className={cn(
        'group flex flex-1 cursor-pointer items-center gap-1.5 overflow-hidden border-b-2 border-transparent text-left transition-opacity hover:opacity-70',
        className,
      )}
      onClick={startEditing}>
      <h1
        className={cn(
          'truncate text-lg font-bold sm:text-2xl',
          isDefault && 'text-muted-foreground italic',
        )}>
        {title}
      </h1>
      <Pencil className="text-muted-foreground size-3.5 shrink-0 opacity-100 transition-opacity group-hover:opacity-100 sm:opacity-0" />
    </button>
  );
};
